import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import { Button, Typography } from "@mui/material";
import { Drawer, Select, TextField, toast } from "@outpave/ds";
import { REPAY_CHARGE_ACCOUNT } from "graphql/account";
import { GET_CARD_PROGRAM_REPAYMENT } from "graphql/cardProgram";
import { BankAccount } from "hooks/useGetBankAccounts";
import { Stack } from "components/layout/reusableLayouts";
import BankLink from "./BankLink";
import { ConfirmTransfer } from "./TransferFormFields";

interface RepaymentSource {
  id: string;
  bankName?: string;
  last4?: string;
}

interface RepayChargeDrawerProps {
  open: boolean;
  /** The charge program being paid down. */
  account?: BankAccount;
  handleCloseDrawer: (refresh?: boolean) => void;
}

const RepayChargeDrawer: React.FC<RepayChargeDrawerProps> = ({
  open,
  account,
  handleCloseDrawer,
}) => {
  const { t } = useTranslation();
  const [repayChargeAccount, { loading }] = useMutation(REPAY_CHARGE_ACCOUNT);

  const programId = account?.cardProgram?.id ?? account?.id;
  const {
    data: repaymentData,
    loading: loadingSources,
    refetch: refetchSources,
  } = useQuery(GET_CARD_PROGRAM_REPAYMENT, {
    variables: { id: programId },
    skip: !open || !programId,
    fetchPolicy: "network-only",
  });
  const sources: RepaymentSource[] =
    repaymentData?.getCardProgram?.repaymentSources ?? [];
  const canLinkSource = Boolean(
    repaymentData?.getCardProgram?.canLinkRepaymentSource,
  );

  const [fromAccount, setFromAccount] = useState("");
  const [amount, setAmount] = useState("");
  const [memo, setMemo] = useState("");
  const [debitAuthorized, setDebitAuthorized] = useState(false);
  const [linking, setLinking] = useState(false);
  // The Select's bottom action takes a handler, and BankLink exposes its opener only through a render prop.
  const openBankLink = useRef<() => void>(() => {});

  // Only opening resets the form. The account is rebuilt on every render of
  // the panel above, and depending on it threw away whatever had been typed.
  useEffect(() => {
    if (!open) return;
    setFromAccount("");
    // The whole outstanding balance is what most people are paying.
    setAmount(
      account?.outstandingAmount != null
        ? String(Math.abs(account.outstandingAmount))
        : "",
    );
    setMemo("");
    setDebitAuthorized(false);
    setLinking(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, account?.id]);

  useEffect(() => {
    if (!sources.some((source) => source.id === fromAccount)) {
      setFromAccount(sources[0]?.id ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repaymentData]);

  const handleSubmit = async () => {
    if (!account || !fromAccount || !amount || !debitAuthorized) return;
    try {
      await repayChargeAccount({
        variables: {
          input: {
            fromAccountId: fromAccount,
            toAccountId: account.id,
            amount: Number(amount),
            debitAuthorized,
            ...(memo.trim() && { memo: memo.trim() }),
          },
        },
      });
      toast.success(t("RepaymentSubmittedToast"));
      handleCloseDrawer(true);
    } catch (error: unknown) {
      toast.error(
        error instanceof Error ? error.message : t("RepaymentUnavailable"),
      );
    }
  };

  return (
    <>
      <Drawer
        open={open}
        disableEnforceFocus={linking}
        onClose={() => handleCloseDrawer()}
        title={t("Pay statement balance")}
        loading={loading}
        footer={
          <>
            <Button
              variant="primary-outlined"
              onClick={() => handleCloseDrawer()}
              disabled={loading}
            >
              {t("Cancel")}
            </Button>
            <Button
              variant="primary-filled"
              onClick={handleSubmit}
              disabled={
                loading || !fromAccount || !Number(amount) || !debitAuthorized
              }
            >
              {t("Pay Now")}
            </Button>
          </>
        }
      >
        <Stack gap="var(--space-4)">
          {account?.outstanding && (
            <Typography
              variant="body2"
              sx={{ color: "var(--color-text-secondary)" }}
            >
              {t("StatementBalanceIs", { amount: account.outstanding })}
            </Typography>
          )}

          <Select
            label={t("Pay from")}
            size="md"
            value={fromAccount || null}
            onChange={(id) => setFromAccount(id ?? "")}
            fullWidth
            loading={loadingSources}
            noOptionsText={t("NoRepaymentBankLinked")}
            options={sources.map((bank) => ({
              id: bank.id,
              label: bank.last4
                ? `${bank.bankName ?? ""} •••• ${bank.last4}`
                : (bank.bankName ?? bank.id),
            }))}
            bottomAction={
              canLinkSource
                ? {
                    label: t("Link a bank account"),
                    onClick: () => openBankLink.current(),
                  }
                : undefined
            }
          />

          <TextField
            label={t("Pay to")}
            value={account?.nickname || account?.bankName || ""}
            disabled
            fullWidth
          />

          <TextField
            label={t("Amount")}
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            inputMode="decimal"
            fullWidth
          />

          <TextField
            label={t("Memo")}
            value={memo}
            onChange={(event) => setMemo(event.target.value)}
            fullWidth
          />

          <ConfirmTransfer
            checked={debitAuthorized}
            onChange={setDebitAuthorized}
            label={t("ChargeRepaymentDebitAuthorization")}
          />
        </Stack>
      </Drawer>

      {open && canLinkSource && (
        <BankLink
          program={account?.cardProgram}
          refetchBankAccounts={refetchSources}
          onBankAdded={setFromAccount}
          onActiveChange={setLinking}
          renderTrigger={(handleOpenAddBank) => {
            openBankLink.current = handleOpenAddBank;
            return null;
          }}
        />
      )}
    </>
  );
};

export default RepayChargeDrawer;
